import { useState } from 'react';

export interface FAQ {
  q: string;
  a: string;
}

interface Props {
  faqs: FAQ[];
  title?: string;
}

export default function SectionFAQ({ faqs, title = 'Frequently Asked Questions' }: Props) {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section className="bg-[#f9f8f4] py-24 relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-[1px]" style={{ background: 'linear-gradient(90deg, transparent, #c9a84c30, transparent)' }} />

      {/* Subtle decorative element */}
      <div className="absolute right-0 top-0 bottom-0 w-1/3 opacity-[0.025]" style={{ background: 'radial-gradient(ellipse at right, #c9a84c, transparent 70%)' }} />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-start">
          {/* Left sticky header */}
          <div className="lg:col-span-4">
            <div className="sticky top-32">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-[2px] bg-[#c9a84c]" />
                <span className="text-[#c9a84c] text-[11px] font-bold uppercase tracking-[0.28em]">Voice Search</span>
              </div>
              <h2
                className="font-bold text-[#0c1520] leading-[1.1] mb-5"
                style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(28px, 3.5vw, 44px)' }}
              >
                {title}
              </h2>
              <div className="flex items-center gap-2.5 mb-7">
                <div className="w-10 h-[3px] bg-[#c9a84c] rounded-full" />
                <div className="w-3 h-[3px] bg-[#c9a84c]/35 rounded-full" />
              </div>
              <p className="text-[#64748b] text-base leading-relaxed mb-8">
                Common questions people ask about criminal defense in Brenham, Texas — optimized for voice search and AI assistants.
              </p>
              <div className="bg-[#050a12] rounded-2xl p-5">
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#c9a84c]/15 border border-[#c9a84c]/20 rounded-lg">
                    <i className="ri-mic-2-line text-[#c9a84c] text-sm" />
                  </div>
                  <p className="text-[#c9a84c] text-xs font-bold uppercase tracking-wider">Voice Search Ready</p>
                </div>
                <p className="text-white/45 text-xs leading-relaxed">These answers are structured for Google Assistant, Siri, and AI search tools.</p>
              </div>
            </div>
          </div>

          {/* Right — accordion */}
          <div className="lg:col-span-8 space-y-3">
            {faqs.map((faq, i) => {
              const isOpen = open === i;
              return (
                <div
                  key={i}
                  className={`group bg-white border rounded-xl overflow-hidden transition-all duration-300 ${
                    isOpen ? 'border-[#c9a84c]/50 shadow-lg shadow-[#c9a84c]/8' : 'border-[#ece9de] hover:border-[#c9a84c]/30'
                  }`}
                >
                  <button
                    onClick={() => setOpen(isOpen ? null : i)}
                    className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer"
                  >
                    <div className="flex items-start gap-3 pr-4">
                      {/* Question number */}
                      <span
                        className={`text-xs font-black flex-shrink-0 mt-0.5 transition-colors duration-300 ${isOpen ? 'text-[#c9a84c]' : 'text-[#c9a84c]/40'}`}
                        style={{ fontFamily: "'Playfair Display', serif" }}
                      >
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <span className={`font-semibold text-base leading-snug transition-colors duration-300 ${isOpen ? 'text-[#0c1520]' : 'text-[#374151]'}`}>
                        {faq.q}
                      </span>
                    </div>
                    <div
                      className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 transition-all duration-300 ${
                        isOpen ? 'bg-[#c9a84c] rotate-180' : 'bg-[#050a12] group-hover:bg-[#0a1428]'
                      }`}
                    >
                      <i className={`ri-arrow-down-s-line text-base ${isOpen ? 'text-[#050a12]' : 'text-[#c9a84c]'}`} />
                    </div>
                  </button>

                  <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-72' : 'max-h-0'}`}>
                    <div className="px-6 pb-6">
                      <div className="flex gap-4">
                        <div className="w-[3px] flex-shrink-0 rounded-full mt-1" style={{ background: 'linear-gradient(to bottom, #c9a84c, #c9a84c30)', minHeight: '100%' }} />
                        <p className="text-[#475569] text-base leading-[1.85]">{faq.a}</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
